/* eslint-disable max-lines */
import { EditOutlined } from '@ant-design/icons'
import { Divider, Grid, Row } from 'antd'
import useDeliveryContactForm from 'app/components/Core/Checkout/hooks/useDeliveryContactForm'
import useLineItemUpdate from 'app/components/Core/Checkout/hooks/useLineItemUpdate'
import useManagerContactForm from 'app/components/Core/Checkout/hooks/useManagerContactForm'
import { useProductIndex } from 'app/components/Core/Checkout/hooks/useProduct'
import CheckoutMap from 'app/components/Core/Checkout/Map'
import ContactDetail from 'app/components/Core/Contact/Detail'
import DeliveryAddressListItem from 'app/components/Core/DeliveryAddress/ListItem'
import LineItemDetail from 'app/components/Core/LineItem/Item/Detail'
import CancelCard from 'app/components/Core/Rental/Card/Cancel'
import useDeliveryAddressForm from 'app/components/Core/Rental/hooks/useDeliveryAddressForm'
import KButton from 'app/components/Modules/General/KButton'
import Button from 'app/components/UI-Elements/General/Button'
import Restricted from 'app/components/UI-Elements/General/Restricted'
import Container from 'app/components/UI-Elements/Layout/Container'
import KCol from 'app/components/UI-Elements/Layout/KCol'
import KFooter from 'app/components/UI-Elements/Layout/KFooter'
import { RecordType } from 'app/constants/GlobalTypes'
import rentalNormalizer from 'app/lib/apollo/normalizers/Rental'
import { useBrowserRouterContext } from 'app/lib/contexts/KBrowserRouter'
import { ToolBox } from 'app/lib/hooks/toolBox'
import { useQuery } from 'app/lib/hooks/useQuery'
import useSetBackHistory from 'app/lib/hooks/useSetBackHistory'
import useTitle from 'app/lib/hooks/useTitle'
import useTranslation from 'app/lib/hooks/useTranslation'
import { notify } from 'app/lib/Notify'
import { PageProps } from 'app/pages/typing'
import { RecordTypeEnum } from 'app/queries'
import * as checkout from 'app/queries/Rentals/Checkout'
import { Routes } from 'app/routes/Routes'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

const RentalUpdate: React.FC<PageProps> = (props) => {
  const { titlelizer } = useTitle(props)
  const { buttonId } = ToolBox.useId()
  const { rental_id } = useParams<{ rental_id: string }>()
  const {
    data: { rental },
    loading
  } = useQuery<{ rental: IRental }>(checkout.RENTAL_CHECKOUT_QUERY, {
    variables: {
      id: rental_id
    },
    normalizer: (data) => ({ rental: rentalNormalizer(data.rental) })
  })
  const { setBackRoute } = useBrowserRouterContext()
  const navigate = useNavigate()

  const { tRecordType, tAttribute, tRoute } = useTranslation({
    scope: props.route.key,
    recordType: RecordTypeEnum.rental
  })

  const { open: openLineItemUpdate } = useLineItemUpdate()
  const managerContact = useManagerContactForm()
  const deliveryContact = useDeliveryContactForm()
  const { md } = Grid.useBreakpoint()
  const { data: lineItems, loading: lineItemsLoading } = useProductIndex({ id: rental_id })

  const { open: openAddressForm } = useDeliveryAddressForm(rental_id)

  useSetBackHistory(() => {
    setBackRoute(Routes.Rental.member(rental_id).show)
  })

  const onButtonClick = () => {
    navigate(Routes.Rental.member(rental_id).show)
  }

  const onSuccess = () => {
    notify({
      intent: 'success',
      position: 'topRight',
      className: 'success-notification',
      message: tRoute({ id: 'updated_success_message' }),
      additionalOptions: { top: 90 }
    })
  }

  const onEdit = (item: ILineItem) => {
    openLineItemUpdate({ lineItemId: item.id, onSuccess, type: 'update_rental' })
  }

  const onAddressEdit = () => {
    openAddressForm({
      member: rental,
      props: {
        formProps: { onSuccess },
        addressInputProps: { showAvailabilityHint: true }
      }
    })
  }

  const onDeliveryContactEdit = () => {
    deliveryContact.open({
      member: rental,
      props: { onSuccess }
    })
  }

  const onManagerContactEdit = () => {
    managerContact.open({
      member: rental,
      props: { onSuccess }
    })
  }

  return (
    <Container>
      <Row gutter={[0, 12]}>
        <KCol loading={loading} xs={0} sm={0} md={0} lg={24}>
          <h3>{titlelizer({ rental_code: rental?.rental_code })}</h3>
        </KCol>
        <KCol span={24}>
          <Row gutter={[24, 12]}>
            <KCol xs={24} md={10}>
              <Row gutter={[24, 12]}>
                <KCol span={24}>
                  <h6 className="mb-0 text-black">{tAttribute({ id: 'addressSummary' })}</h6>
                </KCol>
                <KCol span={24} loading={loading}>
                  <DeliveryAddressListItem
                    member={rental?.deliveryAddress}
                    props={{ onEditClick: onAddressEdit }}
                  />
                </KCol>
                <KCol span={24}>
                  <h6 className="mb-0 text-black">{tRecordType({ id: 'delivery_contact' })}</h6>
                </KCol>
                <KCol span={24} loading={loading}>
                  <ContactDetail
                    props={{
                      extra: (
                        <Button
                          onClick={onDeliveryContactEdit}
                          type="secondary"
                          data-testid={buttonId({
                            recordType: RecordType.delivery_contact,
                            action: 'update'
                          })}
                          icon={<EditOutlined />}
                        />
                      )
                    }}
                    member={rental?.deliveryContact}
                  />
                </KCol>
                <Restricted allowFor={['member']}>
                  <>
                    <KCol span={24}>
                      <h6 className="mb-0 text-black">{tRecordType({ id: 'manager_contact' })}</h6>
                    </KCol>
                    <KCol span={24} loading={loading}>
                      <ContactDetail
                        props={{
                          description: rental?.managerContact?.email,
                          extra: (
                            <Button
                              onClick={onManagerContactEdit}
                              type="secondary"
                              icon={<EditOutlined />}
                            />
                          )
                        }}
                        member={rental?.managerContact}
                      />
                    </KCol>
                  </>
                </Restricted>
              </Row>
            </KCol>
            <KCol xs={24} md={14}>
              <Row gutter={[0, { xs: 12, md: 0 }]}>
                <KCol span={24} />
                <KCol span={24}>
                  <CheckoutMap member={rental} props={{ height: md ? '300px' : '200px' }} />
                </KCol>
              </Row>
            </KCol>
          </Row>
        </KCol>
        <KCol span={24} loading={lineItemsLoading}>
          {lineItems.map((item) => (
            <LineItemDetail
              key={item.id}
              member={item}
              props={{
                onClick: () => onEdit(item),
                showPrice: false,
                showAvailability: false
              }}
            />
          ))}
        </KCol>
        <KCol span={24}>
          <Divider className="mb-0" />
        </KCol>
        <KCol span={24} loading={loading}>
          <CancelCard member={rental} />
        </KCol>
      </Row>
      <KFooter
        sticky={!md}
        style={{ padding: md ? '16px 0 0 0' : undefined, marginTop: 24 }}
        colProps={{ span: !md ? 24 : undefined }}>
        <KButton
          block
          action="back_to_rental_overview"
          recordType={RecordType.rental}
          onClick={onButtonClick}
          type="primary"
        />
      </KFooter>
    </Container>
  )
}

export default RentalUpdate
